import { Fragment, useMemo } from 'react';

import type { MenuItem } from '@/consts/navigation';
import { cn } from '@/utils/cn';

import { NavMenuItemGroup } from './NavMenuItemGroup';
import { NavMenuItemRow } from './NavMenuItemRow';

interface NavMenuItemsListProps {
  items: MenuItem[];
  variant?: 'expanded' | 'popover';
  isUtilitySection?: boolean;
  nested?: boolean;
}

export const NavMenuItemsList = ({
  items,
  variant = 'expanded',
  isUtilitySection = false,
  nested = false
}: NavMenuItemsListProps) => {
  const isPopover = variant === 'popover';

  // Filter out hidden items (used for breadcrumbs only)
  const visibleItems = useMemo(() => items.filter((item) => !item.hidden), [items]);

  return (
    <ul className={cn(!nested && 'flex flex-col gap-0.5 p-1', nested && 'flex flex-col')} role="menu">
      {visibleItems.map((childItem, index) => {
        const visibleChildren = childItem.children?.filter((c) => !c.hidden) ?? [];
        const hasVisibleChildren = visibleChildren.length > 0;
        const isNavigableParent = hasVisibleChildren && childItem.to !== undefined;

        // Pure group header (no route) — e.g. "Voyage Reports" group
        if (hasVisibleChildren && !isNavigableParent) {
          return (
            <NavMenuItemGroup
              key={childItem.text}
              item={childItem}
              isPopover={isPopover}
              isUtilitySection={isUtilitySection}
              isFirst={index === 0}
            />
          );
        }

        // Navigable parent — clickable link with nested children (e.g. PMS libraries)
        if (isNavigableParent) {
          const ParentComponent = childItem.component;
          return (
            <Fragment key={childItem.text}>
              {ParentComponent ? (
                <ParentComponent item={childItem} />
              ) : (
                <NavMenuItemRow childItem={childItem} isPopover={isPopover} isUtilitySection={isUtilitySection} />
              )}
              <NavMenuItemsList items={visibleChildren} variant={variant} isUtilitySection={isUtilitySection} nested />
            </Fragment>
          );
        }

        // Custom component override
        if (childItem.component) {
          const CustomComponent = childItem.component;
          return <CustomComponent key={childItem.text} item={childItem} />;
        }

        // Plain link (no children)
        return (
          <NavMenuItemRow
            key={childItem.text}
            childItem={childItem}
            isPopover={isPopover}
            isUtilitySection={isUtilitySection}
          />
        );
      })}
    </ul>
  );
};
